import { DateTime } from "luxon";
import { isNativePlatform } from "./capacitor/platform";
import { AUDIT_LOG_CATEGORIES, AUDIT_LOG_CATEGORY_KINDS, AUDIT_LOG_CATEGORY_BY_KIND } from "./shared/auditCategories";
import { smartTimeLabel, smartTimeLabels } from "./utils/timeDisplay";

const smartCompare = (valA, valB) => {
  const isNullish = (v) => v === null || v === undefined;
  if (isNullish(valA) && isNullish(valB)) return 0;
  if (isNullish(valA)) return 1;
  if (isNullish(valB)) return -1;

  if (Array.isArray(valA) && Array.isArray(valB)) {
    const len = Math.min(valA.length, valB.length);
    for (let i = 0; i < len; i++) {
      const cmp = smartCompare(valA[i], valB[i]);
      if (cmp !== 0) return cmp;
    }

    return valA.length - valB.length;
  }

  if (typeof valA === "number" && typeof valB === "number") {
    return valA - valB;
  }

  if (valA instanceof Date && valB instanceof Date) {
    return valA.getTime() - valB.getTime();
  }

  // Fallback string comparison
  return valA.toString().localeCompare(valB.toString(), undefined, {
    numeric: true,
    sensitivity: "base",
  });
};

// firstNonBlank picks the first value with a visible character, so a
// whitespace-only field falls through to the next one instead of rendering an
// empty cell.
function firstNonBlank(...values) {
  return values.find((v) => typeof v === "string" && v.trim() !== "") || "";
}

export default {
  statuses: {
    0: { icon: "text-success bg-success/10", name: "msg.granted" },
    1: { icon: "text-danger bg-destructive/10", name: "msg.error" },
    2: { icon: "text-info bg-info/10", name: "msg.pending" },
    3: { icon: "text-danger bg-destructive/10", name: "msg.agentOffline" },
    4: { icon: "text-muted-foreground bg-muted-foreground/10", name: "msg.waiting" },
    5: { icon: "text-success bg-success/10", name: "msg.read" },
    6: { icon: "text-info bg-info/10", name: "msg.published" },
    8: { icon: "text-info bg-info/10", name: "msg.pending" },
    9: { icon: "text-muted-foreground bg-muted-foreground/10", name: "msg.revoked" },
    10: { icon: "text-danger bg-destructive/10", name: "msg.error" },
  },
  timeRemaining: (time) => {
    const date = DateTime.fromISO(time);
    const now = DateTime.now();

    return date.diff(now).milliseconds;
  },
  formatDuration: (ms, _t) => {
    if (ms === null || ms === undefined || ms < 0) {
      return "00:00:00";
    }

    const pad = (n) => Math.floor(n).toString().padStart(2, "0");

    const totalSeconds = Math.floor(ms / 1000);
    const seconds = totalSeconds % 60;
    const totalMinutes = Math.floor(totalSeconds / 60);
    const minutes = totalMinutes % 60;
    const totalHours = Math.floor(totalMinutes / 60);
    const hours = totalHours % 24;
    const days = Math.floor(totalHours / 24);

    if (days > 0) {
      return `${days}:${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
    }
    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  },
  formatTime: (time) => {
    if (time === null || time === undefined) {
      return `00:00:00`;
    } else {
      const d = new Date(time);

      const h = d.getHours().toString().padStart(2, "0");
      const m = d.getMinutes().toString().padStart(2, "0");
      const s = d.getSeconds().toString().padStart(2, "0");

      return `${h}:${m}:${s}`;
    }
  },
  // Coarse human duration for the "more than X days/hours of access" line.
  // Returns days when ms >= 24h, otherwise hours. It floors, and the caller is
  // expected to phrase as "more than {x}" so the rounding-down is safe.
  formatCoarseDuration: (ms, t) => {
    if (ms === null || ms === undefined || ms < 0) return "";
    const HOUR = 60 * 60 * 1000;
    const DAY = 24 * HOUR;
    if (ms >= DAY) {
      const days = Math.floor(ms / DAY);
      return t ? t("msg.durationDays", { count: days }, days) : `${days} ${days === 1 ? "day" : "days"}`;
    }
    const hours = Math.floor(ms / HOUR);
    return t ? t("msg.durationHours", { count: hours }, hours) : `${hours} ${hours === 1 ? "hour" : "hours"}`;
  },
  formatAsDays: (time) => {
    const date = DateTime.fromISO(time);
    const now = DateTime.now();
    const days = Math.floor(date.diff(now).milliseconds / (1000 * 60 * 60 * 24));

    if (days === 1) {
      return "1 day";
    } else if (days === 0) {
      return "today";
    } else if (days == -1) {
      return "1 day ago";
    } else if (days < -1) {
      return `${-days} days ago`;
    } else {
      return `${days} days`;
    }
  },
  marshalDateTime: (date) => {
    if (typeof date === "undefined" || date === "" || date === null) {
      return null;
    }

    return new Date(date).toISOString();
  },
  formatDateTime(date) {
    if (!date) return "";

    const d = new Date(date);
    const year = d.getFullYear();
    const month = (d.getMonth() + 1).toString().padStart(2, "0");
    const day = d.getDate().toString().padStart(2, "0");

    const h = d.getHours().toString().padStart(2, "0");
    const m = d.getMinutes().toString().padStart(2, "0");

    return `${year}-${month}-${day}T${h}:${m}`;
  },
  logEntryActor: (entry) => {
    const kind = Object.keys(entry)[0];
    const data = entry[kind] || {};
    // Session deletions record the acting admin on a dedicated field; rows
    // written before actor attribution lack it and fall through to the
    // generic owner-as-actor behaviour below.
    if (kind === "userSessionDeleted" && data.AdminName) return data.AdminName;
    if (kind === "adminSessionDeleted" && data.DeletedByAdminName) return data.DeletedByAdminName;
    return data.UserName || data.AdminName || "";
  },
  // logEntryActorIdp returns the acting user's login IdP, for the badge telling two
  // accounts of one username apart. Empty for an admin, a local account, or an old row.
  logEntryActorIdp: (entry) => {
    const kind = Object.keys(entry || {})[0];
    const data = entry?.[kind] || {};
    // A session an admin deleted is the admin's action, so the user's provider
    // would label the wrong person.
    if (kind === "userSessionDeleted" && data.AdminName) return "";
    return data.UserName ? data.UserIdpKey || "" : "";
  },
  logEntryAction: (entry) => {
    return "logEntryKind." + Object.keys(entry)[0];
  },
  logEntryIp: (entry) => {
    const first = Object.values(entry || {})[0];
    return first?.IpAddress || first?.RequestingIpAddr || null;
  },
  // logEntryTarget returns a short human-readable label for what the action
  // affected, the ACL/group/knoc/user/agent/api-key on the receiving end of
  // the event, distinct from the actor in logEntryActor. Returns "" when the
  // entry has no meaningful target (e.g. session expiries, simple login/out).
  logEntryTarget: (entry) => {
    if (!entry) return "";
    const kind = Object.keys(entry)[0];
    const data = entry[kind] || {};
    switch (kind) {
      case "createAcl":
      case "updateAcl":
      case "deleteAcl":
        return data.Description || "";
      case "createGroup":
      case "updateGroup":
      case "deleteGroup":
        return data.Name || data.Description || "";
      case "createKnoc":
      case "updateKnoc":
      case "deleteKnoc":
        return data.Name || "";
      // A validation run against an unsaved draft has no name, so it falls
      // back to the backend type, which is all that identifies the draft.
      case "validateIntegration":
        return data.Name || data.Type || "";
      case "saveKnoc":
        return data.KnocName || "";
      case "createUser":
      case "resetUserOtp":
      case "updateUser":
      case "userChangedPassword":
      case "deleteUser":
      case "createAdmin":
      case "resetAdminOtp":
      case "updateAdmin":
      case "deleteAdmin":
        return data.UserName || data.Name || "";
      case "createAgent":
      case "updateAgent":
      case "deleteAgent":
      case "agentAdoptedHostname":
        return data.Name || "";
      case "createApiKey":
      case "updateApiKey":
      case "deleteApiKey":
      case "createAllowlistSecret":
      case "regenerateAllowlistSecret":
      case "hashAllowlistSecret":
      case "updateAllowlistSecret":
        return data.Id ? String(data.Id) : "";
      case "clickedToGrant":
      case "adminManualGrant":
      case "adminOverrideGrant":
      case "greynoiseBlocked":
      case "greynoiseLookupFailed":
      case "threatIntelStaticBlocked":
      case "grantIpExcluded":
      case "discoveredIpFiltered":
        return data.KnocDescription || "";
      case "clickedToRevoke":
      case "grantExpired":
      case "adminRevokeDelegated":
        return data.AclDescription || "";
      case "adminRevokeAgenticAccess":
        return data.Name || data.UserName || "";
      // AgentName is the fallback for rows written before these entries carried the knoc.
      // A whitespace-only description is truthy, so it would win and render blank.
      case "grantError":
      case "revokeError":
        return firstNonBlank(data.KnocDescription, data.AclDescription, data.AgentName);
      // Session owner is the target only when the entry has actor
      // attribution; legacy rows show the owner in the actor column instead.
      case "userSessionDeleted":
        return data.AdminName ? data.UserName || "" : "";
      case "adminSessionDeleted":
        return data.DeletedByAdminName ? data.AdminName || "" : "";
      default:
        return "";
    }
  },
  // logEntryTargetLink returns the admin knoc route for a grant or revoke error
  // row, or null when the row predates KnocId. Never links to the agent.
  logEntryTargetLink: (entry) => {
    if (!entry) return null;
    const kind = Object.keys(entry)[0];
    if (kind !== "grantError" && kind !== "revokeError") return null;
    const knocId = (entry[kind] || {}).KnocId;
    if (typeof knocId !== "string" || knocId.trim() === "") return null;
    // An older server re-marshals an absent id as the nil UUID.
    if (knocId === "00000000-0000-0000-0000-000000000000") return null;
    return { name: "AdminKnocsDetail", params: { knocId } };
  },
  // logEntryCategory groups entry kinds for filter UX and row decoration. The
  // returned key matches a localised label in i18n under
  // `logEntryCategory.<key>`; the colour is a Tailwind class shared by the
  // table dot and the filter chip so the two stay visually consistent.
  logEntryCategory: (kind) => {
    return AUDIT_LOG_CATEGORY_BY_KIND[kind] || "other";
  },
  AUDIT_LOG_CATEGORIES,
  AUDIT_LOG_CATEGORY_KINDS,
  checkJsBundleHash: (v) => {
    // On native, OTA updates are handled by @capgo/capacitor-updater.
    // A page reload in Capacitor just reloads the same local bundle,
    // so the web hash-check reload would be a no-op at best.
    if (isNativePlatform()) return;

    var jsBundleHash = localStorage.getItem("jsBundleHash");
    if (!jsBundleHash) {
      localStorage.setItem("jsBundleHash", v);
    } else if (jsBundleHash !== v) {
      localStorage.setItem("jsBundleHash", v);
      window.location.reload();
    }
  },
  /**
   * Unwrap error and turn it into something user-friendly
   *
   * @param {object} error
   */
  renderError: (t, error) => {
    if (error.response && error.response.data && error.response.data.Err) {
      const prefix = t(error.response.data.Err);
      if (error.response.data.Extra) {
        return `${prefix}: ${
          error.response.data.Extra.startsWith("err.") ? t(error.response.data.Extra) : error.response.data.Extra
        }`;
      }
      return prefix;
    }

    if (error.message) {
      return error.message.startsWith("err.") ? t(error.message) : error.message;
    }

    return "An internal server error occurred";
  },
  renderLastModified: (lastModified, t) => {
    if (lastModified) {
      return t("msg.renderedLastModified", {
        relativeTime: smartTimeLabel(lastModified.At, smartTimeLabels(t)),
        name: lastModified.Name,
      });
    } else {
      return t("msg.unknown");
    }
  },
  btnClass: (theme, hc) => {
    return {
      "bg-theme-teal": theme == "teal" && !hc,
      "bg-theme-teal-hc": theme == "teal" && hc,
      "bg-theme-grape": theme == "grape" && !hc,
      "bg-theme-grape-hc": theme == "grape" && hc,
      "bg-theme-iceberg": theme == "iceberg" && !hc,
      "bg-theme-iceberg-hc": theme == "iceberg" && hc,
      "bg-theme-rose": theme == "rose" && !hc,
      "bg-theme-rose-hc": theme == "rose" && hc,
      "bg-theme-offsite": theme == "offsite" && !hc,
      "bg-theme-offsite-hc": theme == "offsite" && hc,
    };
  },
  sortObjBy: (val) => (obj) => obj[val],
  id: (x) => x,
  smartCompare,
  smartSortFunc: (key) => {
    return (a, b) => smartCompare(key(a), key(b));
  },
  focusFirstError: (node) => {
    // Technically this doesn't always focus the first error, since formkit
    // children aren't garunteed to be in order with conditional rendering.
    // But this is the closest I can get without querying DOM offsets.
    let firstInvalid = null;

    node.walk((child) => {
      const isLeaf = child.children.length === 0;
      const isInvalid = child.ledger.value("blocking");
      if (!firstInvalid && child.props?.id && isInvalid && isLeaf) {
        firstInvalid = child;
      }
    });

    if (firstInvalid?.props?.id) {
      const el = document.getElementById(firstInvalid.props.id);
      el?.focus();
    }
  },
  icons: {},
};
